/**
 * features/quotations/QuotationsListPage.tsx
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Search } from 'lucide-react';
import { QUOTATION_STATUS_LABELS, QUOTATION_STATUSES, formatDate, formatMoney, type QuotationStatus } from '@ft/core';
import { useDebounced } from '~/lib/use-debounced';
import { PageHeader, Pagination, Toolbar } from '~/ui/layout';
import { Button, Card, EmptyState, Input, Select, StatusBadge, TableSkeleton } from '~/ui/primitives';
import { useQuotations } from './hooks';

const PAGE_SIZE = 15;

export function QuotationsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [status, setStatus] = useState<QuotationStatus | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const search = useDebounced(searchInput);

  const { data, isLoading, isFetching } = useQuotations({
    page,
    pageSize: PAGE_SIZE,
    search,
    status: status || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  return (
    <div>
      <PageHeader
        title="ใบเสนอราคา"
        description="สร้างและติดตามใบเสนอราคาที่ส่งให้ลูกค้า"
        actions={
          <Button variant="primary" onClick={() => navigate('/quotations/new')}>
            <Plus size={16} />
            สร้างใบเสนอราคา
          </Button>
        }
      />

      <Card>
        <Toolbar className="flex-wrap gap-2">
          <div className="relative w-full max-w-xs">
            <Search size={15} className="absolute top-1/2 left-2.5 -translate-y-1/2 text-fg-subtle" aria-hidden />
            <Input
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
                setPage(1);
              }}
              placeholder="ค้นหาเลขที่เอกสาร, ชื่อลูกค้า"
              className="pl-8"
            />
          </div>

          <Select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as QuotationStatus | '');
              setPage(1);
            }}
            className="w-40"
          >
            <option value="">ทุกสถานะ</option>
            {QUOTATION_STATUSES.map((value) => (
              <option key={value} value={value}>
                {QUOTATION_STATUS_LABELS[value]}
              </option>
            ))}
          </Select>

          <Input
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              setPage(1);
            }}
            className="w-40"
            aria-label="จากวันที่"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              setPage(1);
            }}
            className="w-40"
            aria-label="ถึงวันที่"
          />

          {isFetching && !isLoading ? <span className="text-xs text-fg-subtle">กำลังอัปเดต…</span> : null}
        </Toolbar>

        {isLoading ? (
          <TableSkeleton columns={5} />
        ) : !data || data.data.length === 0 ? (
          <EmptyState
            icon={<FileText size={28} />}
            title={search || status ? 'ไม่พบใบเสนอราคาที่ค้นหา' : 'ยังไม่มีใบเสนอราคา'}
            description={search || status ? undefined : 'สร้างใบเสนอราคาใบแรกของคุณ'}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>เลขที่เอกสาร</th>
                    <th>ลูกค้า</th>
                    <th>วันที่</th>
                    <th className="text-right">ยอดรวม</th>
                    <th>สถานะ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((quotation) => (
                    <tr
                      key={quotation.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/quotations/${quotation.id}`)}
                    >
                      <td className="font-medium text-fg">{quotation.documentNumber}</td>
                      <td className="text-fg-muted">{quotation.customerName}</td>
                      <td className="tabular text-fg-muted">{formatDate(quotation.date)}</td>
                      <td className="tabular text-right text-fg">{formatMoney(quotation.totalAmount)}</td>
                      <td>
                        <StatusBadge status={quotation.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              pageSize={data.pageSize}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}
